import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { TableCard } from "../shared";
import { onPremLowestCveScoresColumnDefs as columnDefs } from "../col_defs";
import useDashboardService from "../services";
import { toast } from "react-toastify";
import eventEmitter from "../../../components/utils/eventEmitter";
import AssetDetailsDrawer from "../../../components/asset/AssetDetailsDrawer";
import { setSelectedRowID } from "../../../pages/SecurityPosture/TabSelection-slice";
import { useDispatch } from "react-redux";
import { Box, CircularProgress } from "@mui/material";
import { useDashboardContext } from "../DashboardContext";

const PAGE_SIZE = 10;
const SCROLL_THROTTLE_MS = 500;

const OnPremLowestCveScore = () => {
  const { fetchOnPremLowestcveScore } = useDashboardService();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const dispatch = useDispatch();
  const { setIsOverlayOpen } = useDashboardContext();

  // Notify parent context when drawer opens/closes to disable drag and drop
  useEffect(() => {
    setIsOverlayOpen(isDrawerOpen);
  }, [isDrawerOpen, setIsOverlayOpen]);

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });

  const gridRef = useRef<any>(null);
  const isFetchingNext = useRef(false);
  const isFetchingPrev = useRef(false);
  const lastScrollTs = useRef<number>(0);
  const firstVisibleRowRef = useRef<number>(0);
  const scrollPositionRef = useRef<number>(0);
  const scrollCount = useRef(0); // Count scroll events

  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger(prev => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on('filterSaved', handleFilterSaved);

    return () => {
      eventEmitter.off('filterSaved', handleFilterSaved);
    };
  }, []);

  // Initial fetch
  useEffect(() => {
    scrollCount.current = 0; // Reset scroll counter
    isFetchingNext.current = false; // Reset fetching flags
    isFetchingPrev.current = false;

    setTableData([]);
    setPagination({
      page: 1,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
    });
    fetchData({ page: 1, mode: "initial" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const fetchData = useCallback(
    async ({
      page = 1,
      mode = "initial",
    }: {
      page?: number;
      mode?: "initial" | "append" | "prepend";
    } = {}) => {
      // Capture scroll state before fetching
      try {
        const apiBefore = gridRef.current?.api;
        if (apiBefore && mode !== "initial") {
          firstVisibleRowRef.current = typeof apiBefore.getFirstDisplayedRow === "function" ? apiBefore.getFirstDisplayedRow() : firstVisibleRowRef.current;
          const vRangeBefore = apiBefore.getVerticalPixelRange?.();
          scrollPositionRef.current = vRangeBefore?.top ?? scrollPositionRef.current;
        }
      } catch (err) {
        console.warn("Could not capture pre-fetch scroll state for OnPremLowestCveScore", err);
      }

      if (mode === "initial") {
        setLoading(true);
      } else {
        setIsFetchingMore(true);
      }

      try {
        const {
          data,
          pagination: pageInfo,
          error,
        } = await fetchOnPremLowestcveScore({
          page,
          page_size: PAGE_SIZE,
          sort_by: "cve_score",
          sort_order: "asc",
        });

        console.log("📥 API Response:", {
          page,
          dataType: Array.isArray(data) ? "array" : typeof data,
          dataLength: Array.isArray(data) ? data.length : "N/A",
          dataSample: Array.isArray(data) && data.length > 0 ? data[0] : data,
          pageInfo,
          error,
        });

        if (error) {
          toast.error((error as any)?.message || "Failed to load assets with low CVE score");
          return;
        }

        setPagination({
          page: pageInfo?.page || page,
          totalPages: pageInfo?.total_pages || 1,
          hasNext: pageInfo?.has_next || false,
          hasPrevious: pageInfo?.has_previous || false,
        });

        setTableData((prev: any) => {
          if (mode === "initial") {
            console.log("✅ Setting initial data:", data?.length ?? 0, "items");
            return data || [];
          }

          const itemsToAdd = data || [];

          if (mode === "append") {
            console.log(
              "➕ Appending:",
              itemsToAdd.length,
              "items to",
              prev.length,
              "existing"
            );
            return [...prev, ...itemsToAdd];
          }
          if (mode === "prepend") {
            console.log(
              "⬆️ Prepending:",
              itemsToAdd.length,
              "items to",
              prev.length,
              "existing"
            );
            return [...itemsToAdd, ...prev];
          }

          return prev;
        });
        // Restore scroll position after DOM updates
        try {
          const apiAfter = gridRef.current?.api;
          if (apiAfter) {
            if (mode === "append") {
              const indexToShow = firstVisibleRowRef.current ?? 0;
              setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
            } else if (mode === "prepend") {
              const indexToShow = (firstVisibleRowRef.current ?? 0) + (data?.length || 0);
              setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
            }
          }
        } catch (err) {
          console.warn("Could not restore scroll for OnPremLowestCveScore", err);
        }
      } catch (error: any) {
        toast.error(error?.message || "Failed to fetch assets data");
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
        isFetchingNext.current = false;
        isFetchingPrev.current = false;
      }
    },
    [fetchOnPremLowestcveScore]
  );

  // Throttled infinite scroll handler - FIXED: use ref for pagination to avoid recreating
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const handleScrollEnd = useCallback(() => {
    // SIMPLE FIX: Ignore the first scroll event (AG Grid auto-scroll on mount)
    scrollCount.current++;
    if (scrollCount.current === 1) {
      console.log("🚫 Ignored first scroll event (AG Grid auto-scroll)");
      return;
    }

    const now = Date.now();
    if (now - lastScrollTs.current < SCROLL_THROTTLE_MS) {
      return;
    }
    lastScrollTs.current = now;

    const gridApi = gridRef.current?.api;
    if (!gridApi) {
      return;
    }

    try {
      // Get the total row count from the data
      const totalRowCount = tableData.length;
      if (totalRowCount === 0) return;

      // Get viewport info from the event or API
      const verticalPixelRange = gridApi.getVerticalPixelRange?.();

      if (!verticalPixelRange) {
        // Fallback: use simple row count check
        const currentPagination = paginationRef.current;

        // If we have all data loaded and user scrolled, check if we need more
        if (currentPagination.hasNext && !isFetchingNext.current) {
          console.log("⬇️ Loading next page:", currentPagination.page + 1);
          isFetchingNext.current = true;
          fetchData({ page: currentPagination.page + 1, mode: "append" });
        }
        return;
      }

      // Calculate scroll position
      const { top, bottom } = verticalPixelRange;
      const scrollTop = top;
      const scrollHeight =
        gridApi.getModel?.()?.getRowCount?.() * 50 || totalRowCount * 50; // Assume 50px row height
      const clientHeight = bottom - top;

      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      const isNearBottom = scrollPercentage > 0.8; // 80% scrolled
      const isNearTop = scrollTop < 100; // Less than 100px from top

      const currentPagination = paginationRef.current;

      console.log("📜 Scroll:", {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollPercentage: (scrollPercentage * 100).toFixed(1) + "%",
        isNearBottom,
        isNearTop,
        hasNext: currentPagination.hasNext,
        hasPrev: currentPagination.hasPrevious,
      });

      // Load next page when near bottom
      if (
        isNearBottom &&
        currentPagination.hasNext &&
        !isFetchingNext.current
      ) {
        console.log("⬇️ Loading next page:", currentPagination.page + 1);
        isFetchingNext.current = true;
        fetchData({ page: currentPagination.page + 1, mode: "append" });
        return;
      }

      // Load previous page when near top
      if (
        isNearTop &&
        currentPagination.hasPrevious &&
        !isFetchingPrev.current
      ) {
        console.log("⬆️ Loading previous page:", currentPagination.page - 1);
        isFetchingPrev.current = true;
        fetchData({ page: currentPagination.page - 1, mode: "prepend" });
        return;
      }
    } catch (err) {
      console.error("Scroll handler error:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData.length]);

  const memoizedGridOptions = useMemo(
    () => ({
      rowHeight: 50,
      domLayout: "normal",
      suppressScrollOnNewData: true,
      onBodyScrollEnd: handleScrollEnd,
      fullWidthCellRenderer: () => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ),
      isFullWidthRow: (params: any) => params.rowNode?.data?._isLoadingRow === true,
    }),
    [handleScrollEnd]
  );

  // Add loading row at the end when fetching more pages
  const displayData = useMemo(() => {
    if (isFetchingMore) {
      return [...tableData, { _isLoadingRow: true }];
    }
    return tableData;
  }, [tableData, isFetchingMore]);

  const onRowClick = (data: any) => {
    dispatch(setSelectedRowID(data.id));
    setSelectedRow(data);
    setIsDrawerOpen(true);
  };

  return (
    <>
      <TableCard
        title={"Assets with More CVE's"}
        columnDefs={columnDefs}
        rowData={displayData}
        showSearch={false}
        isRowSelectable={false}
        loading={loading}
        onRowClick={(data: any) => {
          // Don't trigger row click for loading row
          if (!data._isLoadingRow) {
            onRowClick(data);
          }
        }}
        gridRef={gridRef}
        gridOptions={memoizedGridOptions}
      />
      
      {/* Loading indicator for pagination
      {isFetchingMore && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 1,
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )} */}

      <AssetDetailsDrawer 
        open={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        assetData={selectedRow} 
        isCloud={false} 
        isCve={true} 
      />
    </>
  );
};

export default OnPremLowestCveScore;
